import React, { useState, useEffect } from 'react';
import { X, Award, FileImage, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../services/api';

const ModalAssociarTemplate = ({ isOpen, onClose, curso, onSuccess }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      carregarTemplates();
    }
  }, [isOpen]);

  const carregarTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/templates');
      const templatesAtivos = response.data.templates?.filter(t => t.ativo) || [];
      setTemplates(templatesAtivos);
      
      // Se o curso já tem um template, selecioná-lo
      if (curso?.template_certificado_id) {
        const templateAtual = templatesAtivos.find(t => t.id === curso.template_certificado_id);
        if (templateAtual) {
          setSelectedTemplate(templateAtual);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      alert('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleAssociar = async () => {
    if (!selectedTemplate) {
      alert('Selecione um template');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/templates/associar-curso', {
        curso_id: curso.id,
        template_id: selectedTemplate.id
      });

      alert('Template associado com sucesso!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao associar template:', error);
      alert('Erro ao associar template');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoverAssociacao = async () => {
    if (!window.confirm('Deseja remover a associação do template com este curso?')) {
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/templates/associar-curso', {
        curso_id: curso.id,
        template_id: null
      });

      alert('Associação removida com sucesso!');
      setSelectedTemplate(null);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao remover associação:', error);
      alert('Erro ao remover associação');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">Associar Template de Certificado</h2>
            <p className="text-slate-400 text-sm mt-1">
              Curso: <span className="text-white font-medium">{curso?.titulo}</span>
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileImage className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Nenhum template disponível</h3>
              <p className="text-slate-400 mb-6">Crie templates de certificados primeiro</p>
              <Button 
                onClick={() => {
                  onClose();
                  // Navegar para criar template
                  window.location.href = '/admin/templates';
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Criar Template
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Template Atual */}
              {curso?.template_certificado_id && (
                <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-blue-300">Template Atual</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRemoverAssociacao}
                      disabled={saving}
                      className="border-red-600/50 text-red-400 hover:bg-red-900/20"
                    >
                      Remover
                    </Button>
                  </div>
                  <p className="text-white text-sm">
                    {templates.find(t => t.id === curso.template_certificado_id)?.nome || 'Template não encontrado'}
                  </p>
                </div>
              )}

              {/* Lista de Templates */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4">Selecionar Template</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <Card 
                      key={template.id} 
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                        selectedTemplate?.id === template.id
                          ? 'bg-blue-900/30 border-blue-500/50 ring-2 ring-blue-500/50'
                          : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800/70'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <CardContent className="p-0">
                        {/* Preview */}
                        <div className="relative h-32 bg-gradient-to-br from-slate-700 to-slate-800 rounded-t-lg flex items-center justify-center">
                          {template.thumbnail ? (
                            <img 
                              src={`http://localhost:5000/uploads/${template.thumbnail}`}
                              alt={template.nome}
                              className="w-full h-full object-cover rounded-t-lg"
                            />
                          ) : (
                            <div className="text-center">
                              <FileImage className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                              <p className="text-slate-400 text-xs">Sem preview</p>
                            </div>
                          )}
                          
                          {/* Badge de Seleção */}
                          {selectedTemplate?.id === template.id && (
                            <div className="absolute top-2 right-2">
                              <div className="bg-blue-500 text-white rounded-full p-1">
                                <Check className="w-4 h-4" />
                              </div>
                            </div>
                          )}

                          {/* Badge de Orientação */}
                          <div className="absolute top-2 left-2">
                            <Badge variant="outline" className="bg-slate-900/80 border-slate-600 text-slate-300 text-xs">
                              {template.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'}
                            </Badge>
                          </div>
                        </div>

                        {/* Informações */}
                        <div className="p-4">
                          <h4 className="font-medium text-white text-sm mb-1 truncate">
                            {template.nome}
                          </h4>
                          {template.descricao && (
                            <p className="text-slate-400 text-xs line-clamp-2 mb-2">
                              {template.descricao}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-xs text-slate-400">
                            <span>{template.width} × {template.height}px</span>
                            <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30">
                              Ativo
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && templates.length > 0 && (
          <div className="flex items-center justify-between p-6 border-t border-slate-700">
            <div className="text-sm text-slate-400">
              {selectedTemplate ? (
                <>Template selecionado: <span className="text-white font-medium">{selectedTemplate.nome}</span></>
              ) : (
                'Selecione um template para associar ao curso'
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAssociar}
                disabled={!selectedTemplate || saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Award className="w-4 h-4 mr-2" />
                {saving ? 'Associando...' : 'Associar Template'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalAssociarTemplate; 